import {ISkRuleObj, SkRule, SkRuleElt, SkRuleEmpty, SkRuleNode, SkRuleStr} from "lib/edit/schema/schemaPatterns";
import {DOM, JSX} from "lib/commons/xml/dom";
import {IGramTransposerProv} from "lib/edit/schema/schema";
import {EGramLevelType, ISkMetaNode, SkMCompo, SkMMeta, SkMPart} from "lib/edit/schema/schemaMeta";
import {SkMInlExtLink, SkMPara, SkMSpan, SkMTxtCell, SkMTxtCol, SkMTxtRow, SkMTxtTable} from "lib/edit/schema/schemaMetaTxt";

export interface OTransposeGramOptions {
	trspProv?: IGramTransposerProv
	abortIfNotInline?: boolean
}

/** Def IGram pour import dynamique. */
export interface IGram {
	readonly gramLevelType: EGramLevelType

	readonly weight: number

	/** Si ce gram corespond à élement html. */
	readonly htmlTag?: string

	readonly semantics?: string[]

	toHtml(p: Node): void

	asHtml(): string
}

/**
 * Principe de transformation d'une source vers un ISkRule root (qui peut être un SkRuleNode ou un SkRuleChoice entre N SkRuleNode, en card ?, +, 1 ou * par ex.) :
 * 1. Transformation de la source en un arbre de Gram (html2gram, sc2gram -> registre de schéma connus ? comment identif le schéma ?)
 * 2. Matching de chaque Gram de l'arbre avec les descendants du ISkRule root,
 *    incluant des distorsions pénalisante (applatissement de profondeurs, réordonnancement de Fields...).
 *    Algo: Arbre de décision, scoring, mesure de proximité grammaticale et sémantique des Gram avec les SkRule-SkMeta
 *    Rétention progressive des IGramTransposition les mieux notées, puis sélection de la meilleur.
 * 3. Construction du DOM cible à partir de l'arbre de ITransposition retenu, respectant les rules et injectant les valeurs issues des gram.
 */
export function transposeGram(gram: IGram, dstRule: SkRule, options?: OTransposeGramOptions): { node: Node; malus: number } | null {
	if (CHRONO) console.time("transposeGram");
	const trspProv = options && options.trspProv || GramTransposerProv.ROOT;
	const targetLevel = findTargetLevel(dstRule);
	let from = gram as GramS;
	if (targetLevel > EGramLevelType.block && gram.gramLevelType !== targetLevel) {
		if (targetLevel === EGramLevelType.inline) {
			if (options && options.abortIfNotInline) return null;
			const it = (gram as GramS).inlineSeq();
			if (it.isEmpty()) return null;
			from = new GramSequence(it);
		} else if (targetLevel === EGramLevelType.text) {
			let it = (gram as GramS).textSeq();
			if (it.isEmpty()) {
				it = (gram as GramS).inlineSeq();
				if (it.isEmpty()) return null;
			}
			from = new GramSequence(it);
		}
	}
	const sol = GramTrspBase.doTranspose(from, dstRule, trspProv);
	if (CHRONO) console.timeEnd("transposeGram");
	if (!sol || sol.nodes === undefined) return null;
	const result = DOM.sharedXmlDoc().createDocumentFragment();
	sol.transposer.buildDst(sol, result);
	if (DEBUG) console.log("transposeGram RESULT:::", sol.malus, DOM.debug(result));
	return {node: result, malus: sol.malus};
}

function findTargetLevel(dstRule: SkRule & IGramLevelTypePointer): EGramLevelType {
	if (!('gramLevelType' in dstRule)) {
		//On cherche le gramLevelType le plus élevé.
		let deepest: EGramLevelType = EGramLevelType.block;
		dstRule.findRule((r: SkRule) => {
			if (r instanceof SkRuleElt) {
				if (r.skMeta.gramLevelType > deepest) deepest = r.skMeta.gramLevelType;
			} else if (r instanceof SkRuleStr) {
				deepest = EGramLevelType.inline;
				return true;
			}
			return false;
		});
		if (Object.isExtensible(dstRule)) dstRule.gramLevelType = deepest;
	}
	return dstRule.gramLevelType;
}

interface IGramLevelTypePointer {
	gramLevelType?: EGramLevelType
}


/**
 * Représentation d'une source dans une structure pivot organisée selon
 * une grammaire commune et générique d'un contenu documentaire.
 */
export abstract class GramS implements IGram, IIterable<GramS> {
	parent: Gram;
	nextSib: GramS;
	prevSib: GramS;

	mapSols: Map<SkRule, IGramTransposition>;

	/** Poids de ce gram (descendant inclus) correspondant au malus s'il devait être éliminé. */
	get weight(): number {return 0}

	/** Niveau logique de ce gram (block par défaut). */
	get gramLevelType(): EGramLevelType {return EGramLevelType.block};

	isEmpty() {return true}

	iterate() {return ITNULL}

	/** Ce gram autorise-t-il les noeuds inline et les noeuds texte en fils. */
	get allowInline(): boolean {return false}

	/** Ce gram autorise-t-il les noeuds para et assimilés (liste, table...) en fils. */
	get allowParaSibling(): boolean {return false}


	/** Ce gram peut-il être assimilé à un para. */
	get isParaSibling(): boolean {return false}

	/**
	 * Retourne tous les gram du niveau juste inférieur, quelquesoit son type.
	 * Un niveau juste inférieur :
	 * - peut-être de même nature que le courant (block, texte, metas, inline).
	 * - peut ne pas être les gram fils directs (ex: table qui liste le contenu de chaque cell, listes...)
	 * - peut être les gram fils directs excepté les metas fils.
	 *
	 */
	subLevelSeq(): IIterable<GramS> {return this}

	/** Construit une séquence de grams de level EGramLevelType.inline. */
	inlineSeq(): IIterable<GramS> {return ITERABLE_EMPTY}

	/** Construit une séquence de grams de level EGramLevelType.text. */
	textSeq(): IIterable<GramS> {return ITERABLE_EMPTY}

	addAsChild(parent: Gram): this {
		parent.append(this);
		return this;
	}

	addAfter(prev: GramS, parent?: Gram): this {
		if (!prev) {
			parent.prepend(this);
		} else {
			if (this.parent) this.rem();
			this.parent = prev.parent;
			this.prevSib = prev;
			if (prev.nextSib) {
				this.nextSib = prev.nextSib;
				this.nextSib.prevSib = this;
			} else {
				this.nextSib = null;
				this.parent.lastSub = this;
			}
			prev.nextSib = this;
		}
		return this;
	}

	addBefore(next: GramS, parent?: Gram): this {
		if (!next) {
			parent.append(this);
		} else {
			if (this.parent) this.rem();
			this.parent = next.parent;
			this.nextSib = next;
			if (next.prevSib) {
				this.prevSib = next.prevSib;
				this.prevSib.nextSib = this;
			} else {
				this.prevSib = null;
				this.parent.firstSub = this;
			}
			next.prevSib = this;
		}
		return this;
	}

	rem(): this {
		if (this.prevSib) {
			this.prevSib.nextSib = this.nextSib;
		} else {
			this.parent.firstSub = this.nextSib;
		}
		if (this.nextSib) {
			this.nextSib.prevSib = this.prevSib;
			this.nextSib = null;
		} else {
			this.parent.lastSub = this.prevSib;
		}
		if (this.prevSib) this.prevSib = null;
		this.parent = null;
		return this;
	}

	abstract toHtml(p: Node): void;

	asHtml(): string {
		const tpl = DOM.sharedHtmlDoc().createElement('template');
		this.toHtml(tpl.content);
		return tpl.innerHTML;
	}

	/**
	 * Ajuste l'arbre pour respecter les règles de couches homogènes blocs / inline notamment.
	 * @return généralement this.nextSib ou le noeud sur lequel appliquer la prochaine normalisation
	 */
	normalize(): GramS {return this.nextSib};
}

export abstract class Gram extends GramS {
	/** Poids d'un noeud logique dans le gram correspondant à un malus s'il est perdu. */
	static NODE_WEIGHT = 10;

	/** Gestion des gram fils. */
	firstSub: GramS;
	lastSub: GramS;

	/**
	 * Tableau ordonné d'associations sémantiques de ce gram.
	 * La 1ère entrée du tableau est le terme le plus précis (nom du tag SC source par exemple).
	 * Les termes suivants sont le libellé, des synonymes connus...
	 */
	semantics?: string[];

	/** Poids de ce contenu correspondant au malus en cas d'echec d'import. */
	get weight(): number {
		let w = Gram.NODE_WEIGHT;
		for (let ch = this.firstSub; ch; ch = ch.nextSib) w += ch.weight;
		return w;
	}


	isEmpty() {return this.firstSub == null}

	* iterate() {
		for (let ch = this.firstSub; ch; ch = ch.nextSib) yield ch;
	}

	inlineSeq(): IIterable<GramS> {return this._inline || (this._inline = new SubInlineIterable(this))}

	protected _inline: IIterable<GramS>;

	textSeq(): IIterable<GramS> {return this._text || (this._text = new SubTextIterable(this))}

	protected _text: IIterable<GramS>;

	append(sub: GramS): this {
		if (sub.parent) sub.rem();
		sub.parent = this;
		sub.prevSib = this.lastSub;
		sub.nextSib = null;
		if (this.lastSub) this.lastSub.nextSib = sub;
		else this.firstSub = sub;
		this.lastSub = sub;
		return this;
	}

	prepend(sub: GramS): this {
		if (sub.parent) sub.rem();
		sub.parent = this;
		sub.prevSib = null;
		sub.nextSib = this.firstSub;
		if (this.firstSub) this.firstSub.prevSib = sub;
		else this.lastSub = sub;
		this.firstSub = sub;
		return this;
	}

	addSemantic(...term: string[]): this {
		if (!this.semantics) this.semantics = term;
		else this.semantics.push(...term);
		return this;
	}

	normalize(): GramS {
		let ch = this.firstSub;
		while (ch) ch = ch.normalize();
		if (this.allowInline) {
			//Fusion des noeuds text consécutifs
			ch = this.firstSub;
			while (ch) {
				if (ch instanceof GramString) {
					while (ch.nextSib instanceof GramString) {
						ch.nextSib.value = (ch as GramString).value + ch.nextSib.value;
						ch = ch.nextSib;
						ch.prevSib.rem();
					}
				}
				ch = ch.nextSib;
			}
		} else if (this.allowParaSibling) {
			wrapInlineInPara(this);
		} else {
			//Cleanup des noeuds textes
			ch = this.firstSub;
			while (ch) {
				const next = ch.nextSib;
				if (ch instanceof GramString) ch.rem();
				ch = next;
			}
		}
		return this.nextSib;
	}

	protected childrenToHtml<N extends Node>(p: N): N {
		for (let ch = this.firstSub; ch; ch = ch.nextSib) ch.toHtml(p);
		return p;
	}

}

/** Racine d'une liste de grams. */
export class GramFragment extends Gram {

	/** indéfini avant normalisation. */
	asInline: undefined | boolean = undefined;

	get allowInline(): boolean {return this.asInline === undefined ? true : this.asInline}

	get allowParaSibling(): boolean {return this.asInline === undefined ? true : !this.asInline}

	get weight(): number {
		let w = 0;
		for (let ch = this.firstSub; ch; ch = ch.nextSib) w += ch.weight;
		return w;
	}

	/** Un fragment devrait être de niveau homogène. => on prend le niveau du 1er fils. */
	get gramLevelType() {return this.firstSub ? this.firstSub.gramLevelType : EGramLevelType.block}

	subLevelSeq(): IIterable<GramS> {return this._subLevel || (this._subLevel = new SubSubLevelIterable(this))}

	protected _subLevel: IIterable<GramS>;

	normalize(): GramS {
		//Homogénisation du level du fragment
		this.asInline = normalizeInlineOrText(this) === EGramLevelType.inline;
		return super.normalize();
	}

	toHtml(p: Node) {this.childrenToHtml(p)}
}

/** Si tous les fils sont inline, reste en inline, sinon enveloppement de chaque séquence d'inline dans un para. */
function normalizeInlineOrText(ctn: Gram): EGramLevelType {
	//Evalue si ce ctn contient au moins un bloc.
	let onlyInline = true;
	for (let ch = ctn.firstSub; ch; ch = ch.nextSib) {
		const lev = ch.gramLevelType;
		if (lev !== EGramLevelType.inline) {
			if (lev === EGramLevelType.metas) continue;
			onlyInline = false;
			break;
		}
	}
	if (!onlyInline) {
		//Au moins un élement non inline, on wrap les inline dans un ou N para.
		wrapInlineInPara(ctn);
		return EGramLevelType.text;
	}
	return EGramLevelType.inline;
}

function wrapInlineInPara(ctn: Gram) {
	let ch = ctn.firstSub;
	while (ch) {
		if (ch.gramLevelType === EGramLevelType.inline) {
			if (ch instanceof GramString && DOM.WHITESPACES.test(ch.value)) {
				//On suppr les noeuds text WS.
				const next = ch.nextSib;
				ch.rem();
				ch = next;
			} else {
				//on wrap tous les noeuds inline consécutifs dans un para
				const para = new GramPara().addBefore(ch);
				let next;
				do {
					next = ch.nextSib;
					para.append(ch);
					ch = next;
				} while (ch && ch.gramLevelType === EGramLevelType.inline);
				while (para.lastSub !== para.firstSub && para.lastSub instanceof GramString && DOM.WHITESPACES.test(para.lastSub.value)) {
					//cleanup des WS en fin de para.
					para.lastSub.rem();
				}
				ch = next;
			}
			continue;
		}
		ch = ch.nextSib;
	}
}


/** Structure non reconnue à préserver. */
export class GramUnknown extends Gram {
	fragment: Element;

	toHtml(p: Node) {p.appendChild(this.fragment)}
}

/** Niveau hiérarchique généralement titré. */
export class GramSection extends Gram {

	get allowParaSibling(): boolean {return true}

	get htmlTag() {return 'section'}

	// Non : pertes des titres dans les blocs non "sub-level" si SubNoMetasIterable.
	// subLevelSeq(): IIterable<GramS> {return this._subLevel || (this._subLevel = new SubNoMetasIterable(this))}
	// protected _subLevel: IIterable<GramS>;

	hasTitle(): boolean {
		for (let ch = this.firstSub; ch; ch = ch.nextSib) if (ch instanceof GramTitle) return true;
		return false;
	}

	toHtml(p: Node) {this.childrenToHtml(p.appendChild(<section/>))}
}

/** Tous les Gram susceptibles d'être placés en frère d'un paragraphe. */
export abstract class GramParaSibling extends Gram {

	get gramLevelType() {return EGramLevelType.text}

	get isParaSibling(): boolean {return true}

	subLevelSeq(): IIterable<GramS> {return this._subLevel || (this._subLevel = new SubNoMetasIterable(this))}

	protected _subLevel: IIterable<GramS>;
}

export class GramPara extends GramParaSibling {

	get allowInline(): boolean {return true}

	get htmlTag() {return 'p'}

	toHtml(p: Node) {this.childrenToHtml(p.appendChild(<p/>))}
}

export class GramList extends GramParaSibling {
	constructor(public ordered: boolean) {
		super();
	}

	get htmlTag() {return this.ordered ? 'ol' : 'ul'}

	get isEachEntryOnePara(): boolean {
		for (let li = this.firstSub; li; li = li.nextSib) {
			if (li instanceof GramListEntry) if (!li.isOnePara) return false;
		}
		return true;
	}

	subLevelSeq(): IIterable<GramS> {return this._subLevel || (this._subLevel = new SubNoMetasSubLevelIterable(this))}

	protected _subLevel: IIterable<GramS>;

	toHtml(p: Node) {this.childrenToHtml(p.appendChild(this.ordered ? <ol/> : <ul/>))}
}

export class GramListEntry extends Gram {

	get gramLevelType() {return EGramLevelType.text}

	get htmlTag() {return 'li'}

	get allowParaSibling(): boolean {return true}

	get isOnePara(): boolean {
		let countP = 0;
		for (let ch = this.firstSub; ch; ch = ch.nextSib) {
			if (ch.gramLevelType === EGramLevelType.metas) continue;
			if (ch instanceof GramPara) {
				if (++countP > 1) return false;
			} else return false;
		}
		return true;
	}

	subLevelSeq(): IIterable<GramS> {return this._subLevel || (this._subLevel = new SubNoMetasIterable(this))}

	protected _subLevel: IIterable<GramS>;

	normalize(): GramS {
		if (this.parent && !(this.parent instanceof GramList)) {
			//GramListEntry pas dans une liste, on wrap
			const newList = new GramList(false).addBefore(this);
			let item: GramS = this;
			do {
				newList.append(item);
				item = newList.nextSib;
			} while (item instanceof GramListEntry);
			newList.normalize();
			return newList.nextSib;
		} else {
			return super.normalize();
		}
	}

	toHtml(p: Node) {this.childrenToHtml(p.appendChild(<li/>))}
}

export class GramTable extends GramParaSibling {

	get htmlTag() {return 'table'}

	toHtml(p: Node) {this.childrenToHtml(p.appendChild(<table/>))}

	subLevelSeq(): IIterable<GramS> {
		class SubIter implements IIterable<GramS> {
			constructor(public gr: GramTable) {}

			isEmpty(): boolean {return this.iterate().next().done}

			* iterate(): IterableIterator<GramS> {
				for (let ch = this.gr.firstSub; ch; ch = ch.nextSib) {
					if (ch instanceof GramTitle) yield ch;
					else if (ch instanceof GramRow) yield* ch.subLevelSeq().iterate();
				}
			}
		}

		return this._subLevel || (this._subLevel = new SubIter(this));
	}

	protected _subLevel: IIterable<GramS>;
}

export class GramCol extends Gram {

	widthInPx?: number;

	get htmlTag() {return 'col'}

	get gramLevelType() {return EGramLevelType.text}

	setWidthInPx(width: string): this {
		this.widthInPx = Math.max(10, parseFloat(width));
		return this;
	}

	toHtml(p: Node) {this.childrenToHtml(p.appendChild(<col width={this.widthInPx > 1 ? this.widthInPx : undefined}/>))}
}

export class GramRow extends Gram {
	get gramLevelType() {return EGramLevelType.text}

	get htmlTag() {return 'tr'}

	subLevelSeq(): IIterable<GramS> {return this._subLevel || (this._subLevel = new SubNoMetasSubLevelIterable(this))}

	protected _subLevel: IIterable<GramS>;

	toHtml(p: Node) {this.childrenToHtml(p.appendChild(<tr/>))}
}

export class GramCell extends Gram {
	spanRow?: number;
	spanCol?: number;

	get htmlTag() {return 'td'}

	get gramLevelType() {return EGramLevelType.text}

	get allowParaSibling(): boolean {return true}

	subLevelSeq(): IIterable<GramS> {return this._subLevel || (this._subLevel = new SubNoMetasIterable(this))}

	protected _subLevel: IIterable<GramS>;

	setSpans(row: string, col: string): this {
		if (row) this.spanRow = parseInt(row) || 1;
		if (col) this.spanCol = parseInt(col) || 1;
		return this;
	}

	toHtml(p: Node) {this.childrenToHtml(p.appendChild(<td rowspan={this.spanRow > 1 ? this.spanRow : undefined} colspan={this.spanCol > 1 ? this.spanCol : undefined}/>))}
}

/**
 * Regroupement logique de fields matérialisant une entité (ex: auteur).
 * L'ordre des fils est peu signifiant (vs GramFieldList).
 */
export class GramPropSet extends Gram {
	get gramLevelType() {return EGramLevelType.metas}

	toHtml(p: Node) {this.childrenToHtml(p.appendChild(<div/>))}
}


/** Liste ordonnée de fields. */
export class GramPropList extends Gram {
	get gramLevelType() {return EGramLevelType.metas}

	toHtml(p: Node) {this.childrenToHtml(p.appendChild(<div/>))}
}

/**
 * Représente un champ élémentaire.
 * La valeur peut-être soit une simple valeur texte terminale, soit constitué de ses grams fils.
 */
export class GramProp extends Gram {
	get gramLevelType() {return EGramLevelType.metas}

	get allowInline(): boolean {return true}

	get allowParaSibling(): boolean {return true}

	getStringValue(): string {
		//XXX deep concnat utile ?
		if (this.firstSub instanceof GramString) return this.firstSub.value;
		return null;
	}

	toHtml(p: Node) {p.appendChild(this.childrenToHtml(<span/>))}
}

/** Champ élémentaire constituant un titre du gram parent. */
export class GramTitle extends GramProp {

	get gramLevelType() {return EGramLevelType.text}

	/** Un titre est ausi vu comme un contenu de type "text". */
	get isParaSibling(): boolean {return true}

	toHtml(p: Node) {
		if (this.parent instanceof GramTable) {
			p.appendChild(this.childrenToHtml(<caption/>));
		} else if (this.parent instanceof GramSection) {
			let level = 1;
			let gramP = this.parent.parent;
			while (level < 6 && gramP && gramP instanceof GramSection) {
				level++;
				gramP = gramP.parent;
			}
			const tag = p.appendChild(document.createElement('h' + level));
			this.childrenToHtml(tag);
		} else {
			super.toHtml(p);
		}
	}
}

/** Champ élémentaire constituant une url pour un lien parent. */
export class GramUrl extends GramProp {

	toHtml(p: Node) {
		if (p instanceof HTMLAnchorElement) p.href = this.getStringValue();
	}
}

/** Structure inline dans un paragraphe. */
export class GramInline extends Gram {
	get gramLevelType() {return EGramLevelType.inline}

	constructor(public htmlEquiv: 'a' | 'b' | 'cite' | 'code' | 'em' | 'i' | 'kbd' | 'q' | 'samp' | 'small' | 'strong' | 'sub' | 'sup') {super()}

	get allowInline(): boolean {return true}

	subLevelSeq(): IIterable<GramS> {return this._subLevel || (this._subLevel = new SubNoMetasIterable(this))}

	protected _subLevel: IIterable<GramS>;

	toHtml(p: Node) {this.childrenToHtml(p.appendChild(document.createElement(this.htmlEquiv)))}
}


/** Noeud text mixedContent. */
export class GramString extends GramS {

	get weight(): number {return this.value.length}

	get gramLevelType() {return EGramLevelType.inline}

	constructor(public value: string) {
		super();
	}

	toHtml(p: Node) {p.appendChild(document.createTextNode(this.value))}
}

/** Représente une séquence de noeuds extrait d'un arbre. */
export class GramSequence extends GramS {

	/** Une séquence devrait être de niveau homogène. => on prend le niveau du 1er fils. */
	get gramLevelType() {
		const first = this.chList.iterate().next().value;
		return first ? first.gramLevelType : EGramLevelType.block;
	}

	get weight(): number {
		let w = 0;
		for (const ch of this.chList.iterate()) w += ch.weight;
		return w;
	}

	constructor(public chList: IIterable<GramS>) {super()}

	subLevelSeq(): IIterable<GramS> {return this._subLevel || (this._subLevel = new SubSeqLevelIter(this.chList))}

	protected _subLevel: IIterable<GramS>;

	inlineSeq(): IIterable<GramS> {return this._inline || (this._inline = new SubSeqInlineIter(this.chList))}

	protected _inline: IIterable<GramS>;

	textSeq(): IIterable<GramS> {return this._text || (this._text = new SubSeqTextIter(this.chList))}

	protected _text: IIterable<GramS>;

	toHtml(p: Node) {for (const ch of this.chList.iterate()) ch.toHtml(p)}
}


class SubSeqLevelIter implements IIterable<GramS> {
	constructor(public chList: IIterable<GramS>) {}

	isEmpty(): boolean {
		if (this.chList.isEmpty()) return true;
		for (const s of this.chList.iterate()) if (!s.subLevelSeq().isEmpty()) return false;
		return true;
	}

	* iterate(): IterableIterator<GramS> {
		for (const s of this.chList.iterate()) yield* s.subLevelSeq().iterate();
	}
}

class SubSeqInlineIter implements IIterable<GramS> {
	constructor(public chList: IIterable<GramS>) {}

	isEmpty(): boolean {
		if (this.chList.isEmpty()) return true;
		for (const s of this.chList.iterate()) if (!s.inlineSeq().isEmpty()) return false;
		return true;
	}

	* iterate(): IterableIterator<GramS> {
		for (const s of this.chList.iterate()) yield* s.inlineSeq().iterate();
	}
}

class SubSeqTextIter implements IIterable<GramS> {
	constructor(public chList: IIterable<GramS>) {}

	isEmpty(): boolean {
		if (this.chList.isEmpty()) return true;
		for (const s of this.chList.iterate()) if (!s.textSeq().isEmpty()) return false;
		return true;
	}

	* iterate(): IterableIterator<GramS> {
		for (const s of this.chList.iterate()) yield* s.textSeq().iterate();
	}
}

/**
 *
 */
export interface IGramTransposer {

	/** Recherche une ou plusieurs solution possible de transposition de ce Gram vers cette rule. */
	transpose(gram: GramS, target: SkRule, trspProv: IGramTransposerProv): IGramTransposition | null

	/**
	 * Construit un DOM résultat à partir d'une transposition précalculée.
	 */
	buildDst(transposition: IGramTransposition, parentDst: Node): void
}

/**
 * Une solution de transposition possible.
 */
export interface IGramTransposition {
	/* Gram à transposer. */
	gram: GramS

	/** Malus de cette transposition. */
	malus: number

	/**
	 * Hiérarchie inversée des noeuds à créer pour concrétiser cette solution.
	 * - null si GramFragment ou GramSequence (pas de création de noeud à ce niveau, mais les fils IGramTransposition.child seront créés)
	 * - undefined si aucune solution (ou arrêt volontaire, les fils IGramTransposition.child ne seront pas créés)
	 */
	nodes: GramNodeSol | null | undefined

	/** Rule cible. Renseigné que si !nodes */
	target?: SkRule

	/** Transposer à utiliser pour obtenir le Dom dst final via IGramTransposer.buildDst(). */
	transposer: IGramTransposer

	/** Transposition du dernier noeud fils. */
	child?: IGramTransposition

	/** Transposition du noeud frère précédent. */
	prevSibTrsp?: IGramTransposition

	/** GramNodeSol d'un des noeuds frères précédents retenu pour l'insertion. */
	prevSibNodeSol?: GramNodeSol
}

/**
 * Représente un noeud à injecter pour créer le DOM cible.
 *
 */
export class GramNodeSol {

	/** Si le IGramTransposition incluant ce GramNodeSol a été retenu, mémoire du noeud DOM parent pour les gram frères suivants. */
	createdParentNode: Node;

	protected _nextRule?: SkRule;

	constructor(public rootRule: SkRule, public nodeRule: ISkRuleObj, public parent?: GramNodeSol) {}

	/**
	 * SkRule possible pour le gram frère suivant.
	 *
	 * Si cette transposition a prévu d'injecter des balises intermédiaires,
	 * autant de nextRules sont possibles pour le gram suivant.
	 *
	 * Soit nextRules une liste issue de la hiérarchie des GramNodeSol.nextRule : S i le gram suivant
	 * est transposé avec nextRules[X], le suivant du suivant peut encore être transposé via les
	 * nextRules[0] à nextRules[X-1] ou via tous les nextRules retournés par le suivant.
	 *
	 * Tableau vide si aucun ajout suivant n'est possible selon le schéma.
	 */
	get nextRule(): SkRule {
		if (!('_nextRule' in this)) this._nextRule = this.rootRule.buildRuleAfter(this.nodeRule);
		return this._nextRule;
	}
}

export class GramNodeSolNull extends GramNodeSol {

	constructor(nextRule: SkRule) {
		super(null, null);
		this._nextRule = nextRule;
	}
}

/**
 * Registre de IGramTransposer classés par type de Gram source.
 */
export class GramTransposerProv extends Map<typeof GramS.constructor, IGramTransposer[]> implements IGramTransposerProv {

	static ROOT = new GramTransposerProv();

	/**
	 * Retourne tous les transposers susceptibles de traiter ce gram.
	 * Les premiers transposers retournés sont à provilégier sur les suivants (si scores équivalents).
	 * XXX A voir stratégie de surcharge par remplacement d'un transposer.
	 */
	transposersFor(gram: GramS): IterableIterator<IGramTransposer> {
		const trsps = this.get(gram.constructor);
		return trsps ? trsps[Symbol.iterator]() : ITNULL;
		// if (v) yield* v;
		// if (this.parentProv) yield* this.parentProv.transposersFor(gram)
	}

	addTransposer(forGram: typeof GramS.constructor, trsp: IGramTransposer) {
		const v = this.get(forGram);
		if (!v) this.set(forGram, [trsp]);
		else v.push(trsp);
	}
}

abstract class GramTrspBase implements IGramTransposer {

	//static QUICKMODE = 0;

	static doTranspose(gram: GramS, target: SkRule, trspProv: IGramTransposerProv): IGramTransposition {
		let best: IGramTransposition;
		if (gram.mapSols) {
			best = gram.mapSols.get(target);
			if (best) return Object.create(best); //Sur-objet pour les props contextuelles prevSibTrsp / prevSibNodeSol
		} else {
			gram.mapSols = new Map();
		}
		if (target !== SkRuleEmpty.SINGLETON) {
			for (const trsp of trspProv.transposersFor(gram)) {
				best = GramTrspBase.best(best, trsp.transpose(gram, target, trspProv));
				if (best && best.malus === 0) break;
			}
			if (!best && gram.gramLevelType <= findTargetLevel(target)) {
				//Aucune sol trouvée, on tente en faisant sauter un level logique du gram.
				let subGram = gram;
				let malus = 0;
				while (!best) {
					malus += Gram.NODE_WEIGHT;
					const subSeq = subGram.subLevelSeq();
					if (subSeq.isEmpty()) break;
					subGram = new GramSequence(subSeq);
					for (const trsp of trspProv.transposersFor(subGram)) best = GramTrspBase.best(best, trsp.transpose(subGram, target, trspProv));
					if (best) {
						best.gram = gram;
						best.malus += malus;
					}
				}
			}
		}
		if (!best) best = {gram, nodes: undefined, target, malus: gram.weight, transposer: GramTrspNone.SINGLETON}; //mémoire de l'absence de solution.
		gram.mapSols.set(target, best);
		return Object.create(best); //Sur-objet pour les props contextuelles prevSibTrsp / prevSibNodeSol
	}

	static best(t1: IGramTransposition, t2: IGramTransposition): IGramTransposition {
		if (t1 == null) return t2;
		if (t2 == null) return t1;
		return t1.malus <= t2.malus ? t1 : t2;
	}

	static addMalus(sol: IGramTransposition, malus: number): IGramTransposition {
		if (sol) sol.malus += malus;
		return sol;
	}

	static sumMalusSiblings(sol: IGramTransposition | null): number {
		let sum = 0;
		while (sol) {
			sum += sol.malus;
			sol = sol.prevSibTrsp;
		}
		return sum;
	}

	static* iterInsertPoints(prevSibNode: GramNodeSol, prevSibTrsp: IGramTransposition): IterableIterator<GramNodeSol> {
		if (!prevSibNode && !prevSibTrsp.prevSibTrsp) yield new GramNodeSolNull(prevSibTrsp.target || prevSibTrsp.nodes.nextRule);
		else {
			for (let node = prevSibNode; node; node = node.parent) yield node;
			if (prevSibTrsp.prevSibTrsp) yield* GramTrspBase.iterInsertPoints(prevSibTrsp.prevSibNodeSol.parent, prevSibTrsp.prevSibTrsp);
		}
	}

	static* iterInsertPointsFromTop(prevSibNode: GramNodeSol, prevSibTrsp: IGramTransposition): IterableIterator<GramNodeSol> {
		if (!prevSibNode && !prevSibTrsp.prevSibTrsp) yield new GramNodeSolNull(prevSibTrsp.target || prevSibTrsp.nodes.nextRule);
		else {
			if (prevSibTrsp.prevSibTrsp) yield* GramTrspBase.iterInsertPointsFromTop(prevSibTrsp.prevSibNodeSol.parent, prevSibTrsp.prevSibTrsp);
			if (prevSibNode) {
				if (!prevSibNode.parent) yield prevSibNode;
				else {
					const stack = [];
					for (let n = prevSibNode; n; n = n.parent) stack.push(n);
					for (let i = stack.length - 1; i >= 0; i--) yield stack[i];
				}
			}
		}
	}

	abstract transpose(gram: GramS, target: SkRule, trspProv: IGramTransposerProv): IGramTransposition | null;

	buildDst(trsp: IGramTransposition & { _nextSibParent?: Node }, parentDst: Node) {
		const parentForCh = this.buildNode(trsp, trsp.nodes, parentDst);
		if (trsp.child) {
			if (!trsp.child.prevSibTrsp) {
				//cas simple: un seul fils
				trsp.child.transposer.buildDst(trsp.child, parentForCh);
			} else {
				//Liste des transposers fils, du dernier au premier
				const trsps = [] as IGramTransposition[];
				for (let ch = trsp.child; ch; ch = ch.prevSibTrsp) trsps.push(ch);
				for (let i = trsps.length - 1; i >= 0; i--) {
					const ch = trsps[i];
					if (ch.nodes !== undefined) {
						ch.transposer.buildDst(ch, ch.prevSibNodeSol ? ch.prevSibNodeSol.createdParentNode || parentForCh : parentForCh);
					}
				}
			}
		}
		//return trsp._nextSibParent; //FIXME return à suppr ?
	}

	protected buildNode(trsp: IGramTransposition & { _nextSibParent?: Node }, nodeSol: GramNodeSol, parentDst: Node): Node {
		if (nodeSol == null) return parentDst;
		if (nodeSol.parent) parentDst = this.buildNode(trsp, nodeSol.parent, parentDst);
		const node = nodeSol.nodeRule instanceof SkRuleElt ? nodeSol.nodeRule.createNode(parentDst) :
			nodeSol.nodeRule instanceof SkRuleStr ? parentDst.ownerDocument.createTextNode((trsp.gram as GramString).value) : null;
		if (node) parentDst.appendChild(node);
		nodeSol.createdParentNode = parentDst;
		return node || parentDst;
	}

	/** Retourne la meilleure transposition pour la série de gram fils. */
	protected transposeChildren(gramParent: Gram, target: SkRule, trspProv: IGramTransposerProv): IGramTransposition | null {
		if (!gramParent.firstSub) return null;
		let sol = GramTrspBase.doTranspose(gramParent.firstSub, target, trspProv);
		for (let ch = gramParent.firstSub.nextSib; ch; ch = ch.nextSib) {
			sol = this.transposeNextChild(ch, sol, trspProv);
		}
		return sol;
	}

	protected transposeSeq(seq: IIterable<GramS>, target: SkRule, trspProv: IGramTransposerProv): IGramTransposition | null {
		if (seq.isEmpty()) return null;
		const it = seq.iterate();
		let sol = GramTrspBase.doTranspose(it.next().value, target, trspProv);
		for (let v = it.next(); !v.done; v = it.next()) {
			sol = this.transposeNextChild(v.value, sol, trspProv);
		}
		return sol;
	}

	protected transposeNextChild(gram: GramS, prevSibTrsp: IGramTransposition, trspProv: IGramTransposerProv): IGramTransposition {
		let best: IGramTransposition;
		if (gram.gramLevelType >= prevSibTrsp.gram.gramLevelType) {
			for (const insPoint of GramTrspBase.iterInsertPoints(prevSibTrsp.nodes, prevSibTrsp)) {
				const newSol = GramTrspBase.doTranspose(gram, insPoint.nextRule, trspProv);
				if (!best || best.malus > newSol.malus) {
					newSol.prevSibTrsp = prevSibTrsp;
					newSol.prevSibNodeSol = insPoint;
					best = newSol;
					if (best.malus === 0) break;
				}
			}
		} else {
			for (const insPoint of GramTrspBase.iterInsertPointsFromTop(prevSibTrsp.nodes, prevSibTrsp)) {
				const newSol = GramTrspBase.doTranspose(gram, insPoint.nextRule, trspProv);
				if (!best || best.malus > newSol.malus) {
					newSol.prevSibTrsp = prevSibTrsp;
					newSol.prevSibNodeSol = insPoint;
					best = newSol;
					if (best.malus === 0) break;
				}
			}
		}
		return best;
	}

	/**
	 * Recherche une transposition dans le contexte parent de parentSol, entre gram et un SkRule fils matchant selectRule
	 * ou tout descendant dont les noeuds intermérdaires matchent throwRule.
	 * La meilleure solution de toutes ces options est retournée.
	 */
	protected findTrsp(gram: Gram, target: SkRule,
										 rootSelectRule: null | ((r: SkRule) => boolean),
										 selectRule: (r: SkRule) => boolean,
										 throwRule: (r: SkRule) => boolean | null,
										 trspProv: IGramTransposerProv,
										 parentSol?: GramNodeSol, cycleGuard?: Map<SkRule, number>
	): IGramTransposition | null {
		let best: IGramTransposition;
		const subRules = target.findRules(rootSelectRule || selectRule) as SkRuleNode[];
		if (subRules) for (let i = 0; i < subRules.length; i++) {
			const subRule = subRules[i];
			if (subRule instanceof SkRuleElt) {
				//On a une solution
				const chTrsp = this.transposeChildren(gram, subRule.contentRule, trspProv);
				best = GramTrspBase.best(best, {
					gram,
					transposer: this,
					malus: subRule.skMeta.computeGramBindingMalus(gram, subRule, GramTrspBase.sumMalusSiblings(chTrsp)),
					child: chTrsp,
					nodes: new GramNodeSol(target, subRule, parentSol)
				});
				if (best.malus === 0) return best;
			} else if (subRule instanceof SkRuleStr) {
				return {
					gram,
					transposer: this,
					malus: 0, // subRule.skMeta.computeGramBindingMalus(gram, subRule, 0),
					nodes: new GramNodeSol(target, subRule, parentSol)
				};
			}
		}
		if (best || !throwRule) return best;
		//Pas trouvé en fils direct, on s'enfonce.
		if (!cycleGuard) cycleGuard = new Map();
		const subRulesThrow = target.findRules(throwRule) as SkRuleElt[];
		if (subRulesThrow) for (let i = 0; i < subRulesThrow.length; i++) {
			const subRule = subRulesThrow[i];
			const count = cycleGuard.get(subRule) || 0;
			if (subRule instanceof SkRuleElt && count < 5) {  //
				cycleGuard.set(subRule, count + 1);
				//console.log("subRule::::", subRule);
				const trsp = this.findTrsp(gram, subRule.contentRule, null, selectRule, throwRule, trspProv, new GramNodeSol(target, subRule, parentSol), cycleGuard);
				if (trsp) {
					trsp.malus = CROSSED_NODE_MALUS + subRule.skMeta.computeGramBindingMalus(gram, subRule, trsp.malus);
					if (trsp.malus === CROSSED_NODE_MALUS) return trsp;
					if (!best || (best.malus > trsp.malus)) best = trsp;
				}
			}
		}
		return best;
	}
}

class GramTrspNone extends GramTrspBase {
	static SINGLETON = new GramTrspNone();

	transpose(gram: GramS, target: SkRule, trspProv: IGramTransposerProv): IGramTransposition | null {
		return null;
	}

	buildDst(trsp: IGramTransposition & { _nextSibParent?: Node }, parentDst: Node): Node {
		return parentDst;
	}
}

/** Trsp de GramFragment. */
class GramTrspFragment extends GramTrspBase {
	transpose(gram: GramFragment, target: SkRule, trspProv: IGramTransposerProv): IGramTransposition | null {
		const chTrsp = this.transposeChildren(gram, target, trspProv);
		if (!isSolFound(chTrsp)) return null; //aucune sol trouvée
		return {
			gram: gram,
			nodes: null,
			target: target,
			malus: GramTrspBase.sumMalusSiblings(chTrsp), // ? target.skMeta.computeGramBindingMalus(gram, target, GramTrspBase.sumMalusSiblings(chTrsp)),
			child: chTrsp,
			transposer: this
		};
	}
}

GramTransposerProv.ROOT.addTransposer(GramFragment, new GramTrspFragment());

/** Trsp de GramFragment en tentant d'envelopper ses N fils dans une section racine. */
class GramTrspFragmentWrapped extends GramTrspBase {
	transpose(gram: GramFragment, target: SkRule, trspProv: IGramTransposerProv): IGramTransposition | null {
		if (!gram.firstSub || !gram.firstSub.nextSib) return null;
		//Au moins deux frères, une meilleur solution pourrait exister en wrappant le tout d'une section
		const root = new GramSection();
		while (gram.firstSub) root.append(gram.firstSub);
		try {
			const chTrsp = GramTrspBase.doTranspose(root, target, trspProv);
			if (!isSolFound(chTrsp)) return null;
			return {
				gram: gram,
				nodes: null,
				target: target,
				malus: CROSSED_NODE_MALUS + GramTrspBase.sumMalusSiblings(chTrsp), //? target.skMeta.computeGramBindingMalus(gram, target, GramTrspBase.sumMalusSiblings(chTrsp));
				child: chTrsp,
				transposer: this
			};
		} finally {
			//On restaure le fragement.
			while (root.firstSub) gram.append(root.firstSub);
		}
	}
}

GramTransposerProv.ROOT.addTransposer(GramFragment, new GramTrspFragmentWrapped());

class GramTrspSequence extends GramTrspBase {
	transpose(gram: GramSequence, target: SkRule, trspProv: IGramTransposerProv): IGramTransposition | null {
		const chTrsp = this.transposeSeq(gram.chList, target, trspProv);
		if (!isSolFound(chTrsp)) return null; //aucune sol trouvée
		return {
			gram: gram,
			nodes: null,
			target: target,
			malus: GramTrspBase.sumMalusSiblings(chTrsp), //? target.skMeta.computeGramBindingMalus(gram, target, GramTrspBase.sumMalusSiblings(chTrsp));
			child: chTrsp,
			transposer: this
		};
	}
}

GramTransposerProv.ROOT.addTransposer(GramSequence, new GramTrspSequence());

/** Trsp de GramPara vers une rule avec skMeta de type paragraphe. */
class GramTrspPara extends GramTrspBase {
	transpose(gram: Gram, target: SkRule, trspProv: IGramTransposerProv): IGramTransposition | null {
		return this.findTrsp(gram, target,
			null,
			(rule: SkRule) => rule.skMeta instanceof SkMPara,
			throwRule4ParaSibling,
			trspProv
		);
	}
}

GramTransposerProv.ROOT.addTransposer(GramPara, new GramTrspPara());

/** Trsp de GramList vers une rule avec skMeta de type OL / UL / SL. */
class GramTrspList extends GramTrspBase {
	transpose(gram: GramList, target: SkRule, trspProv: IGramTransposerProv): IGramTransposition | null {
		const ruleId = gram.ordered ? 'OL' : (gram.isEachEntryOnePara ? 'SL' : 'UL');
		let sol = this.findTrsp(gram, target,
			null,
			(rule: SkRule) => rule instanceof SkRuleElt && rule.skMeta.id === ruleId,
			throwRule4ParaSibling,
			trspProv
		);
		if (sol) return sol;
		const ruleId2 = ruleId === 'OL' ? 'UL' : ruleId === 'UL' ? 'OL' : 'UL';
		sol = this.findTrsp(gram, target,
			null,
			(rule: SkRule) => rule instanceof SkRuleElt && rule.skMeta.id === ruleId2,
			throwRule4ParaSibling,
			trspProv
		);
		if (sol) return sol;
		const ruleId3 = ruleId === 'SL' ? 'OL' : 'SL';
		return this.findTrsp(gram, target,
			null,
			(rule: SkRule) => rule instanceof SkRuleElt && rule.skMeta.id === ruleId3,
			throwRule4ParaSibling,
			trspProv
		);
	}
}

GramTransposerProv.ROOT.addTransposer(GramList, new GramTrspList());


/** Trsp de GramListEntry vers une rule avec skMeta de type LI (multi-paragraphes). */
class GramTrspListEntry extends GramTrspBase {
	transpose(gram: GramListEntry, target: SkRule, trspProv: IGramTransposerProv): IGramTransposition | null {
		return this.findTrsp(gram, target,
			null,
			(rule: SkRule) => rule instanceof SkRuleElt && rule.skMeta.id === 'LI',
			null,
			trspProv
		);
	}
}

GramTransposerProv.ROOT.addTransposer(GramListEntry, new GramTrspListEntry());


/** Trsp de GramList vers une rule en dehors du texte riche (listes modélisées en parts / fields / choices d'assmnt...). */
class GramTrspList2Struct extends GramTrspBase {
	transpose(gram: GramSection, target: SkRule, trspProv: IGramTransposerProv): IGramTransposition | null {
		return this.findTrsp(gram, target,
			null,
			(rule: SkRule) => rule instanceof SkRuleElt && (rule.skMeta as ISkMetaAsList).asGramList === 'list',
			(rule: SkRule) => !rule.skFamily && (rule.skMeta instanceof SkMCompo || rule.skMeta instanceof SkMPart),
			trspProv
		);
	}
}

GramTransposerProv.ROOT.addTransposer(GramList, new GramTrspList2Struct());

/** A implémenter par les SkMeta pour être associable à GramTrspList2Struct et GramTrspListEntry2Struct. */
export interface ISkMetaAsList extends ISkMetaNode {
	asGramList: "list" | "entry"
}

/** Trsp de GramListEntry pour les items fils bindés par GramTrspList2Struct. */
class GramTrspListEntry2Struct extends GramTrspBase {
	static SINGLETON = new GramTrspListEntry2Struct();

	transpose(gram: GramListEntry, target: SkRule, trspProv: IGramTransposerProv): IGramTransposition | null {
		return this.findTrsp(gram, target,
			null,
			(rule: SkRule) => rule instanceof SkRuleElt && (rule.skMeta as ISkMetaAsList).asGramList === 'entry',
			null,
			trspProv
		);
	}
}

GramTransposerProv.ROOT.addTransposer(GramListEntry, GramTrspListEntry2Struct.SINGLETON);

/** Trsp de GramListEntry vers une rule avec skMeta de type SLMember  (mono-paragraphe). */
class GramTrspListEntry2SL extends GramTrspBase {
	transpose(gram: GramListEntry, target: SkRule, trspProv: IGramTransposerProv): IGramTransposition | null {
		return this.findTrsp(gram, target,
			null,
			(rule: SkRule) => rule instanceof SkRuleElt && rule.skMeta.id === 'SLMember',
			null,
			trspProv
		);
	}

	protected transposeChildren(gramParent: GramListEntry, target: SkRule, trspProv: IGramTransposerProv): IGramTransposition | null {
		if (!gramParent.firstSub) return null;
		return super.transposeChildren(gramParent.firstSub as GramPara, target, trspProv);
	}
}

GramTransposerProv.ROOT.addTransposer(GramListEntry, new GramTrspListEntry2SL());

/** Trsp de GramTable vers une rule avec skMeta de type table. */
class GramTrspTable extends GramTrspBase {
	transpose(gram: GramTable, target: SkRule, trspProv: IGramTransposerProv): IGramTransposition | null {
		return this.findTrsp(gram, target,
			null,
			(rule: SkRule) => rule.skMeta instanceof SkMTxtTable,
			throwRule4ParaSibling,
			trspProv
		);
	}
}

GramTransposerProv.ROOT.addTransposer(GramTable, new GramTrspTable());

/** Trsp de GramCol vers une rule avec skMeta de type col. */
class GramTrspCol extends GramTrspBase {
	transpose(gram: GramCol, target: SkRule, trspProv: IGramTransposerProv): IGramTransposition | null {
		return this.findTrsp(gram, target,
			null,
			(rule: SkRule) => rule.skMeta instanceof SkMTxtCol,
			null,
			trspProv
		);
	}

	protected buildNode(trsp: IGramTransposition & { _nextSibParent?: Node }, nodeSol: GramNodeSol, parentDst: Node): Node {
		const node = super.buildNode(trsp, nodeSol, parentDst) as Element;
		if (nodeSol.nodeRule.skMeta instanceof SkMTxtCol) {
			nodeSol.nodeRule.skMeta.setWidthInPx(nodeSol.nodeRule, node, (trsp.gram as GramCol).widthInPx || 100);
		}
		return node;
	}
}

GramTransposerProv.ROOT.addTransposer(GramCol, new GramTrspCol());

/** Trsp de GramRow vers une rule avec skMeta de type row. */
class GramTrspRow extends GramTrspBase {
	transpose(gram: GramRow, target: SkRule, trspProv: IGramTransposerProv): IGramTransposition | null {
		return this.findTrsp(gram, target,
			null,
			(rule: SkRule) => rule.skMeta instanceof SkMTxtRow,
			null,
			trspProv
		);
	}
}

GramTransposerProv.ROOT.addTransposer(GramRow, new GramTrspRow());

/** Trsp de GramCell vers une rule avec skMeta de type cell. */
class GramTrspCell extends GramTrspBase {
	transpose(gram: GramCell, target: SkRule, trspProv: IGramTransposerProv): IGramTransposition | null {
		return this.findTrsp(gram, target,
			null,
			(rule: SkRule) => rule.skMeta instanceof SkMTxtCell,
			null,
			trspProv
		);
	}

	protected buildNode(trsp: IGramTransposition & { _nextSibParent?: Node }, nodeSol: GramNodeSol, parentDst: Node): Node {
		const node = super.buildNode(trsp, nodeSol, parentDst) as Element;
		const skM = nodeSol.nodeRule.skMeta;
		if (skM instanceof SkMTxtCell) {
			const gram = trsp.gram as GramCell;
			if (gram.spanRow > 1) skM.setRowSpan(nodeSol.nodeRule, node, gram.spanRow);
			if (gram.spanCol > 1) skM.setColSpan(nodeSol.nodeRule, node, gram.spanCol);
		}
		return node;
	}
}

GramTransposerProv.ROOT.addTransposer(GramCell, new GramTrspCell());


/** Trsp de GramInline vers une rule avec skMeta de type span. */
class GramTrspInline extends GramTrspBase {
	transpose(gram: GramInline, target: SkRule, trspProv: IGramTransposerProv): IGramTransposition | null {
		return this.findTrsp(gram, target,
			null,
			(rule: SkRule) => rule.skMeta instanceof SkMSpan && this.matchRuleFamily(gram, rule),
			null,
			trspProv
		);
	}

	matchRuleFamily(gram: GramInline, rule: SkRule): boolean {
		switch (gram.htmlEquiv) {
		case "b":
		case "strong":
			return rule.skFamily === "b" || rule.skFamily === "strong";
		case "i":
		case "em":
			return rule.skFamily === "i" || rule.skFamily === "em";
		default:
			return rule.skFamily === gram.htmlEquiv;
		}
	}
}

GramTransposerProv.ROOT.addTransposer(GramInline, new GramTrspInline());

/** Trsp de GramSection vers une rule de family sub-level. */
class GramTrspSection extends GramTrspBase {
	transpose(gram: GramSection, target: SkRule, trspProv: IGramTransposerProv): IGramTransposition | null {
		return this.findTrsp(gram, target,
			(rule: SkRule) => rule.skFamily === 'sub-level' || (rule.skMeta instanceof SkMCompo),
			(rule: SkRule) => rule.skFamily === 'sub-level',
			(rule: SkRule) => !rule.skFamily && (rule.skMeta instanceof SkMCompo || rule.skMeta instanceof SkMPart),
			trspProv
		);
	}
}

GramTransposerProv.ROOT.addTransposer(GramSection, new GramTrspSection());

/** Trsp de GramTitle vers une rule avec skMeta de famille titre. */
class GramTrspTitle extends GramTrspBase {
	transpose(gram: GramTitle, target: SkRule, trspProv: IGramTransposerProv): IGramTransposition | null {
		return this.findTrsp(gram, target,
			null,
			(rule: SkRule) => rule.skFamily === "property/title",
			(rule: SkRule) => rule.skMeta instanceof SkMPart || rule.skMeta instanceof SkMCompo || rule.skMeta instanceof SkMMeta,
			trspProv
		);
	}
}

GramTransposerProv.ROOT.addTransposer(GramTitle, new GramTrspTitle());

/** Trsp de GramTitle vers une rule avec skMeta de type paragraphe avec Malus. */
class GramTrspTitle2Para extends GramTrspPara {
	transpose(gram: GramTitle, target: SkRule, trspProv: IGramTransposerProv): IGramTransposition | null {
		//Perte d'un typage de titre : proportionnel au volume du contenu de la section déstructurée.
		//mais a minima, on perd le poids de noeud typé "titre".
		return GramTrspTitle2Para.addMalus(super.transpose(gram, target, trspProv), Math.max(Gram.NODE_WEIGHT, gram.parent ? gram.parent.weight / 3 : gram.weight / 3));
	}
}

GramTransposerProv.ROOT.addTransposer(GramTitle, new GramTrspTitle2Para());


/** Trsp de GramUrl vers une rule avec skMeta de famille url. */
class GramTrspUrl extends GramTrspBase {
	transpose(gram: GramUrl, target: SkRule, trspProv: IGramTransposerProv): IGramTransposition | null {
		return this.findTrsp(gram, target,
			null,
			(rule: SkRule) => rule.skFamily === "property/url",
			(rule: SkRule) => rule.skMeta instanceof SkMMeta,
			trspProv
		) || {gram, target, nodes: undefined, malus: gram.weight, transposer: GramTrspNone.SINGLETON}; //On interdit de sauter ce gram pour publier l'URL directement.
	}
}

GramTransposerProv.ROOT.addTransposer(GramUrl, new GramTrspUrl());

/** Trsp de GramUrl vers une rule avec skMeta de type InlExtLink (uLink de type externalUrl). */
class GramTrspUrlUlink extends GramTrspBase {
	transpose(gram: GramUrl, target: SkRule, trspProv: IGramTransposerProv): IGramTransposition | null {
		return this.findTrsp(gram, target,
			null,
			(rule: SkRule) => rule.skMeta instanceof SkMInlExtLink,
			null,
			trspProv
		);
	}

	buildDst(trsp: IGramTransposition & { _nextSibParent?: Node }, parentDst: Node) {
		if (parentDst instanceof Element) parentDst.setAttribute("url", (trsp.gram as GramUrl).getStringValue() || "");
	}
}

GramTransposerProv.ROOT.addTransposer(GramUrl, new GramTrspUrlUlink());


/** Trsp de GramString vers une rule avec skMeta de famille titre. */
class GramTrspString extends GramTrspBase {
	transpose(gram: Gram, target: SkRule, trspProv: IGramTransposerProv): IGramTransposition | null {
		return this.findTrspString(gram, target, trspProv, new Set(), null);
	}

	protected findTrspString(gram: GramS, target: SkRule, trspProv: IGramTransposerProv, cycleGuard: Set<SkRule>, parentSol: GramNodeSol): IGramTransposition | null {
		const subRule = target.findRule((rule: SkRule) => rule instanceof SkRuleStr) as SkRuleStr;
		if (subRule) return {
			gram,
			transposer: this,
			malus: 0, //subRule.skMeta.computeGramBindingMalus(gram, subRule, 0),
			nodes: new GramNodeSol(target, subRule, parentSol)
		};
		//Pas trouvé en fils direct, on s'enfonce sauf si on entre dans un élément de type property ou property/url.
		const subNodes = target.findRules((rule: SkRule) => {
			return rule instanceof SkRuleElt && !(rule.skFamily === "property" || rule.skFamily === "property/url");
		}) as SkRuleElt[];
		if (subNodes) for (let i = 0; i < subNodes.length; i++) {
			const subNode = subNodes[i];
			if (!cycleGuard.has(subNode)) {
				cycleGuard.add(subNode);
				//console.log("subNode::::", subNode);
				const sol = this.findTrspString(gram, subNode.contentRule, trspProv, cycleGuard, new GramNodeSol(target, subNode, parentSol));
				if (sol) {
					sol.malus++;
					return sol;
				}
			}
		}
		return null;
	}
}

GramTransposerProv.ROOT.addTransposer(GramString, new GramTrspString());

export interface IIterable<T> {
	isEmpty(): boolean

	iterate(): IterableIterator<T>
}

class SubSubLevelIterable implements IIterable<GramS> {
	constructor(public gram: Gram) {}

	isEmpty(): boolean {
		for (let s = this.gram.firstSub; s; s = s.nextSib) if (!s.subLevelSeq().isEmpty()) return false;
		return true;
	}

	* iterate(): IterableIterator<GramS> {
		for (let s = this.gram.firstSub; s; s = s.nextSib) {
			const subSeq = s.subLevelSeq();
			if (!subSeq.isEmpty()) yield* subSeq.iterate();
		}
	}
}

class SubNoMetasSubLevelIterable implements IIterable<GramS> {
	constructor(public gram: Gram) {}

	isEmpty(): boolean {
		for (let s = this.gram.firstSub; s; s = s.nextSib) {
			if (s.gramLevelType !== EGramLevelType.metas && !s.subLevelSeq().isEmpty()) return false;
		}
		return true;
	}

	* iterate(): IterableIterator<GramS> {
		for (let s = this.gram.firstSub; s; s = s.nextSib) {
			if (s.gramLevelType !== EGramLevelType.metas) {
				const subSeq = s.subLevelSeq();
				if (!subSeq.isEmpty()) yield* subSeq.iterate();
			}
		}
	}
}

class SubInlineIterable implements IIterable<GramS> {
	constructor(public gram: Gram) {}

	isEmpty(): boolean {
		for (let s = this.gram.firstSub; s; s = s.nextSib) {
			const level = s.gramLevelType;
			if (level === EGramLevelType.inline) return false;
			if (level !== EGramLevelType.metas && !s.inlineSeq().isEmpty()) return false;
		}
		return true;
	}

	* iterate(): IterableIterator<GramS> {
		for (let s = this.gram.firstSub; s; s = s.nextSib) {
			const level = s.gramLevelType;
			if (level === EGramLevelType.inline) yield s;
			else if (level !== EGramLevelType.metas) {
				const seq = s.inlineSeq();
				if (!seq.isEmpty()) yield* seq.iterate();
			}
		}
	}
}

class SubTextIterable implements IIterable<GramS> {
	constructor(public gram: Gram) {}

	isEmpty(): boolean {
		for (let s = this.gram.firstSub; s; s = s.nextSib) {
			if (s.isParaSibling) return false;
			const level = s.gramLevelType;
			if (level !== EGramLevelType.metas && level !== EGramLevelType.inline && !s.textSeq().isEmpty()) return false;
		}
		return true;
	}

	* iterate(): IterableIterator<GramS> {
		for (let s = this.gram.firstSub; s; s = s.nextSib) {
			if (s.isParaSibling) yield s;
			else {
				const level = s.gramLevelType;
				if (level !== EGramLevelType.metas && level !== EGramLevelType.inline) {
					const seq = s.textSeq();
					if (!seq.isEmpty()) yield* seq.iterate();
				}
			}
		}
	}
}

/** Liste les fils en excluant les metas. */
class SubNoMetasIterable implements IIterable<GramS> {
	constructor(public gram: Gram) {}

	isEmpty(): boolean {
		for (let s = this.gram.firstSub; s; s = s.nextSib) {
			if (s.gramLevelType !== EGramLevelType.metas) return false;
		}
		return true;
	}

	* iterate(): IterableIterator<GramS> {
		for (let s = this.gram.firstSub; s; s = s.nextSib) {
			if (s.gramLevelType !== EGramLevelType.metas) yield s;
		}
	}
}


function throwRule4ParaSibling(rule: SkRule) {
	return (!rule.skFamily || !rule.skFamily.startsWith('prop')) && !(rule.skMeta instanceof SkMPara);
}

/** Evalue si une sol ou un de ses frères précédents (sol.prevSibTrsp) a trouvé quelque-chose. */
function isSolFound(sol: IGramTransposition): boolean {
	if (!sol) return false;
	while (sol) {
		if (sol.nodes !== undefined) return true;
		sol = sol.prevSibTrsp;
	}
	return true;
}


const ITERABLE_EMPTY = {
	isEmpty() {return true},
	iterate() {return ITNULL}
} as IIterable<any>;

const ITNULL: IterableIterator<any> = {
	next() {return ITRESULT_DONE},
	[Symbol.iterator](): IterableIterator<any> {return ITNULL}
};
const ITRESULT_DONE = Object.freeze({done: true, value: undefined}) as IteratorResult<any>;

/** Malus pour un noeud cible traversé. */
const CROSSED_NODE_MALUS = 1;

const DEBUG = false; //true
const CHRONO = false; //true